import { HelpCircle } from "lucide-react";
import { Link } from "react-router";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { CoachingUser } from "@/types/users";
import { findNextEvenWeekTuesday } from "@/utils/datetime";
import { formatCredits } from "@/utils/string";

interface CreditBalanceBoxProps {
  label: string;
  amount?: string;
  tooltip?: string;
  linkComponent?: React.ReactNode;
  showLinks?: boolean;
  className?: string;
}

function CreditBalanceBox({
  label,
  amount = "0.00",
  tooltip,
  linkComponent,
  showLinks = false,
  className,
}: CreditBalanceBoxProps) {
  return (
    <Card className={cn("whitespace-nowrap text-black flex-1", className)}>
      <CardContent>
        <div className="text-sm font-semibold text-gray-600 flex items-center">
          {label}
          {tooltip && (
            <SimpleTooltip content={<div>{tooltip}</div>}>
              <button className="ml-2.5 flex items-center justify-center w-5 h-5 rounded-full bg-gray-500 text-white text-xs">
                <HelpCircle className="w-3 h-3" />
              </button>
            </SimpleTooltip>
          )}
        </div>
        <div className="text-lg font-medium mt-1">{formatCredits(amount)}</div>
        {showLinks && (
          <div className="flex mt-6 text-center text-sm">{linkComponent}</div>
        )}
      </CardContent>
    </Card>
  );
}

interface WorkspaceCreditsProps {
  user: CoachingUser;
  ctaButton: React.ReactNode;
  secondaryButton: React.ReactNode;
}

export function WorkspaceCredits({
  user,
  ctaButton,
  secondaryButton,
}: WorkspaceCreditsProps) {
  const { availableCredits } = user;

  return (
    <div className="w-full">
      <div className="flex pb-3 items-center justify-between">
        <div className="flex text-xl font-semibold items-center">
          <EmbedSvg src={CreditSVG} height={24} width={24} />
          <div className="ml-2">Credit Overview</div>
        </div>
        <div>
          {ctaButton}
          {secondaryButton}
        </div>
      </div>
      <div className="flex flex-col md:flex-row">
        <CreditBalanceBox
          label="CREDIT BALANCE"
          tooltip="These credits have been purchased and are unspent"
          amount={availableCredits ?? "0.00"}
          className="md:mr-2 mb-2 md:mb-0"
        />
      </div>
    </div>
  );
}

interface CoachCreditsProps {
  user: CoachingUser;
  showLinks?: boolean;
}

export function CoachCredits({ user, showLinks = true }: CoachCreditsProps) {
  const { availableCredits, receivableCredits } = user;

  return (
    <div className="w-full py-2">
      <div className="flex pb-3 text-xl font-semibold items-center">
        <EmbedSvg src={CreditSVG} height={24} width={24} />
        <div className="ml-2">Credit Overview</div>
      </div>
      <div className="flex flex-col md:flex-row">
        <CreditBalanceBox
          label="PENDING PAYOUT"
          tooltip={`Payouts are processed every other Tuesday. The next one is on ${findNextEvenWeekTuesday()}`}
          amount={receivableCredits ?? "0.00"}
          showLinks={showLinks}
          linkComponent={
            <>
              <Link to="/payouts">Payout History</Link>
              <div className="mx-2">·</div>
              <Link to="/transfer">Transfer Credits</Link>
            </>
          }
          className="md:mr-2 mb-2 md:mb-0"
        />
        <CreditBalanceBox
          label="AVAILABLE TO SPEND"
          tooltip="You can either transfer credits you have earned or buy credits
                  to spend them on the platform."
          amount={availableCredits ?? "0.00"}
          showLinks={showLinks}
          linkComponent={<Link to="/buy">Buy Credits</Link>}
          className="md:ml-2"
        />
      </div>
    </div>
  );
}

interface AvailableCreditsContainerProps {
  ctaButton: React.ReactNode;
  secondaryButton: React.ReactNode;
  title?: string;
  creditAmount?: string;
  loading?: boolean;
}

export function AvailableCreditsContainer({
  ctaButton,
  secondaryButton,
  title = "AVAILABLE TO YOU",
  creditAmount = "0",
  loading = false,
}: AvailableCreditsContainerProps) {
  return (
    <div className="flex justify-between gap-2 w-full">
      <div className="flex items-center">
        <div className="block xs:hidden">
          <EmbedSvg src={CreditSVG} height={36} width={36} />
        </div>
        <div className="hidden xs:block">
          <EmbedSvg src={CreditSVG} height={57} width={57} />
        </div>
        <div className="py-2 ml-3">
          <div className="text-sm font-semibold bg-gradient-to-b from-[#ffc712] to-[#e09d02] bg-clip-text text-transparent">
            {title}
          </div>
          <div className="mt-1 text-lg md:text-xl font-semibold">
            {loading ? (
              <Skeleton className="w-12 h-6">3.0</Skeleton>
            ) : (
              formatCredits(creditAmount)
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center flex-wrap">
        {ctaButton}
        {secondaryButton}
      </div>
    </div>
  );
}

interface AvailableCreditsProps {
  user: CoachingUser;
  ctaLabel?: string;
  ctaLink?: string;
  secondaryLabel?: string;
  secondaryLink?: string;
  title?: string;
  className?: string;
}

export function AvailableCredits({
  user,
  ctaLabel,
  ctaLink,
  secondaryLabel,
  secondaryLink,
  title = "AVAILABLE TO YOU",
  className,
}: AvailableCreditsProps) {
  const ctaButton =
    ctaLink && ctaLabel ? (
      <div className="mt-3 md:mt-0 w-full md:w-auto">
        <Button asChild intent="secondary">
          <Link to={ctaLink}>{ctaLabel}</Link>
        </Button>
      </div>
    ) : null;

  const secondaryButton =
    secondaryLabel && secondaryLink ? (
      <div className="md:ml-3 mt-3 md:mt-0 w-full md:w-auto">
        <Button asChild intent="secondary">
          <Link to={secondaryLink}>{secondaryLabel}</Link>
        </Button>
      </div>
    ) : null;

  return (
    <Card className={className}>
      <CardContent>
        {user.isAgent ? (
          <CoachCredits user={user} />
        ) : (
          <AvailableCreditsContainer
            title={title}
            creditAmount={user.availableCredits ?? "0.00"}
            ctaButton={ctaButton}
            secondaryButton={secondaryButton}
          />
        )}
      </CardContent>
    </Card>
  );
}
